import React, { useState } from 'react';
import { useNavigate } from '../router';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Lock, VpnKey } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ColorModeToggle from '../components/ColorModeToggle';

export default function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Box
      sx={{
        minHeight: 'var(--rs-vh)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'transparent',
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <ColorModeToggle placement="bottom" />
      </Box>
      <Card
        sx={{
          maxWidth: 440,
          width: '100%',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.5)'
            : '0 8px 32px rgba(15, 23, 42, 0.12)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#ff9800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <VpnKey sx={{ fontSize: 32, color: '#0a0c11' }} />
            </Box>
            <Typography variant="h5" sx={{ color: '#ff9800', fontWeight: 'bold', mb: 1 }}>
              Change Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.username}, you must change your temporary password before continuing.
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Your account was created with a temporary password. For security, please set a new password now.
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Current (Temporary) Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: <Lock sx={{ color: 'text.disabled', mr: 1 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'text.primary',
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: '#ff9800' },
                  '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              helperText="Minimum 8 characters"
              InputProps={{
                startAdornment: <VpnKey sx={{ color: 'text.disabled', mr: 1 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'text.primary',
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: '#ff9800' },
                  '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
                '& .MuiFormHelperText-root': { color: 'text.disabled' },
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              error={confirmPassword !== '' && confirmPassword !== newPassword}
              helperText={confirmPassword !== '' && confirmPassword !== newPassword ? 'Passwords do not match' : ''}
              InputProps={{
                startAdornment: <VpnKey sx={{ color: 'text.disabled', mr: 1 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'text.primary',
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: '#ff9800' },
                  '&.Mui-focused fieldset': { borderColor: '#ff9800' },
                },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 1,
                bgcolor: '#ff9800',
                color: '#0a0c11',
                fontWeight: 'bold',
                py: 1.5,
                '&:hover': { bgcolor: '#f57c00' },
                '&:disabled': { bgcolor: 'var(--rs-border)', color: '#666' },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#0a0c11' }} /> : 'Set New Password'}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={handleLogout}
              sx={{ color: '#666', '&:hover': { color: '#ff6b6b' } }}
            >
              Log Out Instead
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
